/**
 * Scaleo click attribution (CORE-4767).
 *
 * Scaleo appends `clkid` / `clktype` when a visitor lands from an affiliate
 * placement (e.g. ShopBack). Those values belong to the visit they arrived in:
 * if a later organic visit inherits them, OG Rewards books the lead as an
 * affiliate conversion and the affiliate is over-paid.
 *
 * They are therefore stored on the session attribution record — which already
 * carries the 30-minute inactivity window — instead of the long-lived
 * `tracking` cookie that holds the UTMs. UTM behaviour is deliberately
 * unchanged; only the Scaleo pair is session-scoped.
 */

export type ScaleoClickParams = {
  clkid: string | null;
  clktype: string | null;
};

export const SCALEO_PARAM_NAMES: readonly string[] = ['clkid', 'clktype'];

/**
 * Scaleo does not publish a format for these values, so validation only rejects
 * what cannot be a click identifier — empty, over-long, or carrying characters
 * that would need escaping once forwarded into a provider redirect URL. Widen
 * these if a legitimate value is ever seen being rejected in the wild.
 */
const CLKID_PATTERN = /^[A-Za-z0-9_-]{1,128}$/;
const CLKTYPE_PATTERN = /^[A-Za-z0-9_-]{1,32}$/;

/** GA4 event emitted when a landing carries Scaleo params we refuse to store. */
export const SCALEO_REJECTED_EVENT = 'scaleo_click_capture_rejected';

export type ScaleoParseResult = {
  /** The validated pair, or `null` when absent or rejected. */
  params: ScaleoClickParams | null;
  /**
   * Why a present value was refused, or `null`. Returned rather than logged so
   * this stays a pure function: it runs on every read (each lead submit, and on
   * the server), while the AC asks for one event per *landing*. Only the capture
   * path in `sessionAttributionTracker` reports it.
   */
  rejection: string | null;
};

/**
 * Read the Scaleo pair out of a query string.
 *
 * `params` is `null` when the query string carries neither param — the caller
 * then leaves whatever the session already holds untouched. When at least one is
 * present the pair is returned as a unit, so a landing that carries only `clkid`
 * yields `clktype: null` rather than inheriting an older `clktype`.
 *
 * A malformed value rejects the whole pair: a click id we cannot trust makes the
 * accompanying click type meaningless.
 */
export function parseScaleoClickParams(search: string): ScaleoParseResult {
  let params: URLSearchParams;
  try {
    params = new URLSearchParams(search);
  } catch {
    return { params: null, rejection: null };
  }

  const clkid = params.get('clkid');
  const clktype = params.get('clktype');
  if (clkid == null && clktype == null) {
    return { params: null, rejection: null };
  }

  if (clkid != null && !CLKID_PATTERN.test(clkid)) {
    return { params: null, rejection: 'invalid_clkid' };
  }
  if (clktype != null && !CLKTYPE_PATTERN.test(clktype)) {
    return { params: null, rejection: 'invalid_clktype' };
  }

  return { params: { clkid, clktype }, rejection: null };
}

/**
 * Validate a pair already held in storage before it is used. Guards against a
 * record written by an older build, or hand-edited cookie content.
 */
export function sanitizeScaleoClickParams(
  value: ScaleoClickParams | null | undefined,
): ScaleoClickParams | null {
  if (!value) return null;
  const clkid =
    typeof value.clkid === 'string' && CLKID_PATTERN.test(value.clkid)
      ? value.clkid
      : null;
  const clktype =
    typeof value.clktype === 'string' && CLKTYPE_PATTERN.test(value.clktype)
      ? value.clktype
      : null;
  if (clkid == null && clktype == null) return null;
  return { clkid, clktype };
}
